/**
 * Auto-start, resume and restore decisions from the server's versions
 * (PROTOTYPER_STABILITY_TECH_PLAN.md P6.4c) instead of the browser's list of built turns.
 * The browser lists (`builtTurnIds`, the Chef project id in `sessionStorage`) only decide
 * while the versions API does not answer.
 *
 * Kept dependency-free so it stays unit-testable in the node test env.
 */

export interface TurnVersion {
  id: string;
  llmInteractionId: string | null;
  status: string;
  fixAllowed?: boolean;
  fixAttempts: number;
  fixAttemptsGranted: number;
  leaseOwner: string | null;
  leaseExpiresAt: string | null;
  chefProjectId?: string | null;
  prototypeId?: string | null;
  /**
   * Its number within its prototype (V1, V2 ...), given by the backend when the build finishes;
   * null while unfinished. The pill and the published link `/p/<slug>/v<number>` show it.
   */
  number?: number | null;
  createdAt: string;
}

/**
 * Whether the turn's pill can show a number yet, from its version alone. `undefined` when the
 * version does not decide it (no version known for the turn).
 */
export function versionHasNumber(version: TurnVersion | undefined): boolean | undefined {
  if (!version) return undefined;
  if (version.status === 'verified') return true;
  // Numbers are given only to finished versions, so an unfinished one never shows one, even
  // after its stream reported success and before its verify landed.
  return typeof version.number === 'number' && version.number > 0;
}

/**
 * Why a turn's build is no longer listed, from its version: `removed` by the founder (Remove, or
 * "Undo this change"), or `replaced` when a newer build of the same prototype finished.
 */
export type VersionSetAside = 'removed' | 'replaced';

export function versionSetAside(status: string | undefined): VersionSetAside | undefined {
  if (status === 'discarded') return 'removed';
  if (status === 'superseded') return 'replaced';
  return undefined;
}

export type TurnVersionsStatus = 'loading' | 'ready' | 'unavailable';

export interface TurnVersionsLookup {
  status: TurnVersionsStatus;
  get: (llmInteractionId: string) => TurnVersion | undefined;
}

/** One version per turn: the newest (the API lists newest first; order is re-checked here). */
export function indexTurnVersions(versions: ReadonlyArray<TurnVersion>): Map<string, TurnVersion> {
  const byTurn = new Map<string, TurnVersion>();
  for (const version of versions) {
    const lid = version.llmInteractionId;
    if (!lid) continue;
    const existing = byTurn.get(lid);
    if (!existing || version.createdAt > existing.createdAt) byTurn.set(lid, version);
  }
  return byTurn;
}

/** Another tab or device holds the version's build lease right now. */
export function isLeasedElsewhere(
  version: Pick<TurnVersion, 'leaseOwner' | 'leaseExpiresAt'>,
  ownerTabId: string,
  now: number = Date.now()
): boolean {
  if (!version.leaseOwner || version.leaseOwner === ownerTabId || !version.leaseExpiresAt) {
    return false;
  }
  const expires = Date.parse(version.leaseExpiresAt);
  return Number.isFinite(expires) && expires > now;
}

const DONE_STATUSES: ReadonlySet<string> = new Set([
  'verified',
  'discarded',
  'superseded',
  // The "needs your input" card asks the founder (P6.4b); nothing starts on its own.
  'failed_needs_input',
]);

/**
 * Decisions that need neither the snapshot index nor the bootstrap restore, so they are taken
 * before waiting for either. `skip` when the turn is done, failed without an attempt left, or
 * is being built by another tab.
 */
export function settledTurnVersionDecision(
  version: TurnVersion,
  ownerTabId: string,
  now: number = Date.now()
): 'skip' | null {
  if (DONE_STATUSES.has(version.status)) return 'skip';
  if (version.status === 'failed' && version.fixAllowed !== true) return 'skip';
  if (version.status === 'draft' && isLeasedElsewhere(version, ownerTabId, now)) return 'skip';
  return null;
}

/**
 * Once the snapshot index and bootstrap settled:
 * - `failed` with an attempt left: `resume-fix` — restore the turn's files and start the fix
 *   stream. Never rebuild first: the same error would count as a repeat and stop fixing.
 *   Without the turn's files there is nothing to fix in the browser, so `skip`.
 * - `draft` with the turn's files: `resume-build` (the build was interrupted).
 * - `draft` without them: `resume-from-files` — build the files the builder saved for this turn
 *   (P6.4d-3); when it has none, the caller shows the retry state.
 * - otherwise null: the plan-message rules decide (`unfinished` / `start`).
 */
export function resumeTurnVersionDecision(
  version: TurnVersion,
  hasSnapshotEntry: boolean
): 'resume-fix' | 'resume-build' | 'resume-from-files' | 'skip' | null {
  if (version.status === 'failed' && version.fixAllowed === true) {
    return hasSnapshotEntry ? 'resume-fix' : 'skip';
  }
  // P6.4d-3: without a build-start snapshot, build the files the builder saved for this turn.
  if (version.status === 'draft') return hasSnapshotEntry ? 'resume-build' : 'resume-from-files';
  return null;
}

type SnapshotEntryLike = { llmInteractionId: string; buildVerified?: boolean; createdAt?: string };

/**
 * Snapshots a restore may use, newest first: never a turn that was discarded ("Undo this
 * change") or superseded once versions answer.
 */
export function restorableEntries<E extends SnapshotEntryLike>(
  entries: ReadonlyArray<E>,
  versions?: TurnVersionsLookup
): E[] {
  const ready = versions?.status === 'ready';
  return [...entries]
    .filter((entry) => {
      if (!ready) return true;
      const status = versions!.get(entry.llmInteractionId)?.status;
      return status !== 'discarded' && status !== 'superseded';
    })
    .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
}

/**
 * The snapshot the bootstrap restores after a reload: the newest verified restorable one, else
 * the newest restorable one.
 */
export function pickBootstrapEntry<E extends SnapshotEntryLike>(
  entries: ReadonlyArray<E>,
  versions?: TurnVersionsLookup
): E | undefined {
  const ready = versions?.status === 'ready';
  const byNewest = restorableEntries(entries, versions);
  const verified = (entry: E): boolean =>
    entry.buildVerified === true ||
    (ready && versions!.get(entry.llmInteractionId)?.status === 'verified');
  return byNewest.find(verified) ?? byNewest[0];
}

/**
 * The newest verified version of the prototype this tab has loaded, when it is newer than the
 * loaded one (P6.4e): another tab or device moved the prototype on, so a new turn must not start
 * from this tab's older files. Null when the loaded version is current or unknown.
 */
export function newerVerifiedVersion(
  versions: ReadonlyArray<TurnVersion>,
  loadedLlmInteractionId: string | null | undefined
): TurnVersion | null {
  if (!loadedLlmInteractionId) return null;
  const loaded = versions.find((version) => version.llmInteractionId === loadedLlmInteractionId);
  if (!loaded?.prototypeId) return null;
  let newest: TurnVersion | null = null;
  for (const version of versions) {
    if (version.prototypeId !== loaded.prototypeId || version.status !== 'verified') continue;
    if (!version.llmInteractionId || version.createdAt <= loaded.createdAt) continue;
    if (!newest || version.createdAt > newest.createdAt) newest = version;
  }
  return newest;
}

/** What the one button on an unfinished build does (Task 722). */
export type UnfinishedBuildAction = 'continue' | 'build-again';

/**
 * A build that was only interrupted (a draft: Stop, a closed tab, a dropped connection) can be
 * finished from where it stopped, so it gets Continue. One whose build failed is broken, and
 * continuing would carry the broken files on: it gets Build again, which starts the request
 * over from the prototype's last finished version.
 */
export function unfinishedBuildAction(status: string | undefined): UnfinishedBuildAction {
  return status === 'failed' || status === 'failed_needs_input' ? 'build-again' : 'continue';
}
